import type { FC, ReactNode } from 'react'

import type { SortingMode } from '@skytech/utils'
import MenuDownIcon from 'mdi-react/MenuDownIcon'
import MenuUpIcon from 'mdi-react/MenuUpIcon'
import { twJoin } from 'tailwind-merge'

export const Th = <T extends string>({
  id,
  children,
  className,
  isSortable = true,
  onClick,
}: {
  id?: T
  children?: ReactNode
  className?: string
  isSortable?: boolean
  onClick?: (id: T, sortingMode: SortingMode) => void
}) => {
  const search = new URLSearchParams(location.search)
  const sortingModeFromParams = search.get('sort') as SortingMode
  const sortBy = search.get('sortBy') || 'off'
  const isSortedBy = sortBy === id
  const isSortingOff = sortingModeFromParams === 'off' || (sortBy && sortBy !== id)
  const sortingMode = isSortedBy ? (isSortingOff ? 'off' : sortingModeFromParams) : 'off'

  // Id and onClick are optional but if one exists, another should too
  if (Boolean(id) !== Boolean(onClick))
    throw Error('Sortable table header requires both id and onClick!')

  // Regular table header (no sorting)
  if (!isSortable || !id) return <th className={className}>{children}</th>

  return (
    <th className={className}>
      <button
        className="border-none bg-transparent text-base font-bold text-zinc-900 hover:cursor-pointer"
        onClick={() => {
          if (!onClick)
            throw Error(
              'Column header is expected to provide sorting but no callback has been passed!'
            )
          onClick(id, getUpdatedSortingMode(sortingMode))
        }}
      >
        <div className="flex items-center gap-0.5">
          {children}
          <SortingIcon sortingMode={sortingMode} />
        </div>
      </button>
    </th>
  )
}

export const SortingIcon: FC<{ sortingMode: SortingMode }> = ({ sortingMode }) => (
  <div className="flex flex-col">
    <MenuUpIcon
      className={twJoin(
        '-mb-4 !h-6 !w-6',
        ['desc', 'off'].includes(sortingMode) && 'fill-zinc-400'
      )}
    />
    {/* todo: Remove ! after button is refactored to use tailwind */}
    <MenuDownIcon
      className={twJoin('!h-6 !w-6', ['asc', 'off'].includes(sortingMode) && 'fill-zinc-400')}
    />
  </div>
)

function getUpdatedSortingMode(sortingMode: SortingMode) {
  switch (sortingMode) {
    case 'off':
      return 'asc'
    case 'asc':
      return 'desc'
    default:
      return 'off'
  }
}
